import React, { useState } from 'react';
import { X, Coins } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { ApiService, Token } from '../../../services/api';
import { SignedTransactionService } from '../../../services/signedTransactionService';
import toast from 'react-hot-toast';

interface MintTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: Token | null;
  onSuccess: () => void;
}

const MintTokenModal: React.FC<MintTokenModalProps> = ({ isOpen, onClose, token, onSuccess }) => {
  const { connected, publicKey, signTransaction } = useWallet();
  
  const [amount, setAmount] = useState('');
  const [destinationWallet, setDestinationWallet] = useState('');
  const [isMinting, setIsMinting] = useState(false);

  if (!isOpen || !token) return null;

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!amount || !destinationWallet) {
      toast.error('Please fill in all fields');
      return;
    }

    const mintAmount = parseFloat(amount);
    if (mintAmount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    setIsMinting(true);

    try {
      const wallet = {
        publicKey,
        signTransaction,
      };

      // Build the transaction
      const buildResponse = await ApiService.buildMintTokensTransaction({
        tokenId: token.id,
        amount: mintAmount,
        destinationWallet,
        walletAddress: publicKey.toString(),
      });

      // Sign and send transaction
      const result = await SignedTransactionService.signAndSendTransaction(
        buildResponse.transaction,
        wallet
      );

      if (result.success && result.signature) {
        // Save the result to backend
        await ApiService.saveMintTokensResult({
          tokenId: token.id,
          amount: mintAmount,
          solanaSignature: result.signature,
        });

        toast.success(`Successfully minted ${mintAmount} ${token.symbol} tokens`);
        
        // Reset form
        setAmount('');
        setDestinationWallet('');
        
        onSuccess();
        onClose();
      } else {
        throw new Error(result.error || 'Transaction failed');
      }
    } catch (error: any) {
      console.error('Failed to mint tokens:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to mint tokens');
    } finally {
      setIsMinting(false);
    }
  };

  const handleClose = () => {
    if (!isMinting) {
      setAmount('');
      setDestinationWallet('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75"
          onClick={handleClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-[#182233] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
            <div className="flex items-center">
              <Coins className="h-6 w-6 text-[#2777EF] mr-2" />
              <h3 className="text-lg font-medium text-white">
                Mint {token.symbol} Tokens
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isMinting}
              className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleMint} className="px-6 py-4 space-y-4">
            {/* Token Info */}
            <div className="bg-[#0D1421] rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Token</span>
                <span className="text-sm font-medium text-white">{token.name}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Symbol</span>
                <span className="text-sm font-medium text-white">{token.symbol}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Current Supply</span>
                <span className="text-sm font-medium text-white">
                  {(token.currentSupply / Math.pow(10, token.decimals)).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Decimals</span>
                <span className="text-sm font-medium text-white">{token.decimals}</span>
              </div>
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Amount to Mint *
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`e.g., 1000`}
                min="0"
                step="any"
                className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF]"
                required
                disabled={isMinting}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the amount in human-readable format (e.g., 1000 for 1000 tokens)
              </p>
            </div>

            {/* Destination Wallet Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Destination Wallet Address *
              </label>
              <input
                type="text"
                value={destinationWallet}
                onChange={(e) => setDestinationWallet(e.target.value)}
                placeholder="Enter Solana wallet address"
                className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-[#2777EF]"
                required
                disabled={isMinting}
              />
              <p className="mt-1 text-xs text-gray-500">
                The wallet address that will receive the minted tokens
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isMinting}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isMinting}
                className="px-4 py-2 bg-[#2777EF] text-white rounded-lg hover:bg-[#1F5FD3] transition-colors disabled:opacity-50 flex items-center"
              >
                {isMinting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Minting...
                  </>
                ) : (
                  <>
                    <Coins className="mr-2 h-4 w-4" />
                    Mint Tokens
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MintTokenModal;

